import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Turnstile } from '@marsidev/react-turnstile'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useAuth } from '../hooks/useAuth'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Mail, Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { getErrorMessage } from '@/lib/utils'
import { useState, useEffect, useRef } from 'react'
import { env } from '@/config/env'
// DEV-ONLY masquerade widget (local scaffolding — do not ship). Tree-shaken out
// of production builds via the import.meta.env.DEV guard below.
import { DevMasquerade } from '@/dev/DevMasquerade'
import { supabaseAuth, type SsoProvider } from '../api/supabaseAuth'

const signInSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
})

type SignInFormData = z.infer<typeof signInSchema>

export function SignIn() {
  const { signIn, isSigningIn, signInError } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [showTurnstileDialog, setShowTurnstileDialog] = useState(false)
  const [turnstileStatus, setTurnstileStatus] = useState<'pending' | 'verifying' | 'success' | 'error'>('pending')
  const pendingCredentials = useRef<SignInFormData | null>(null)
  // The form element itself, so submit can read the values the BROWSER holds rather than
  // only the ones React saw. These inputs are controlled (react-hook-form `control` +
  // `{...field}`), and a password manager that writes straight to the DOM without firing
  // React's synthetic change event leaves component state stale — the classic autofill
  // desync. Production logs showed sign-ins arriving with no password at all.
  const formRef = useRef<HTMLFormElement | null>(null)

  // Which provider is mid-redirect, so both buttons disable and the one clicked can say so.
  const [ssoProvider, setSsoProvider] = useState<SsoProvider | null>(null)
  const [ssoError, setSsoError] = useState<string | null>(null)

  /**
   * Hand off to the provider. This navigates away, so there is no success path to handle here —
   * the browser returns to /auth/callback with a session, and AuthCallback finishes the job.
   *
   * A provider that is not enabled in the Supabase dashboard fails here rather than silently
   * doing nothing, which is what the placeholder buttons used to do.
   */
  const startSso = async (provider: SsoProvider) => {
    setSsoError(null)
    setSsoProvider(provider)
    try {
      await supabaseAuth.signInWithProvider(provider)
    } catch (e) {
      setSsoProvider(null)
      setSsoError(
        `${provider === 'google' ? 'Google' : 'Facebook'} sign-in is unavailable. ` +
        'Please use your email and password.',
      )
      console.error('[auth] sso failed', e)
    }
  }

  const form = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  // Handle form submission - show Turnstile dialog if configured
  /**
   * Prefer what the browser actually has in the field over what React recorded.
   *
   * Only used when the two disagree and the DOM has more — an autofill that never
   * reached component state. Typing always wins, because then they agree.
   */
  const fromDom = (name: keyof SignInFormData, fallback: string) => {
    const el = formRef.current
      ? formRef.current.querySelector<HTMLInputElement>(`input[name="${name}"]`)
      : null
    const domValue = el && typeof el.value === 'string' ? el.value : ''
    return !fallback && domValue ? domValue : fallback
  }

  const onSubmit = (raw: SignInFormData) => {
    const data: SignInFormData = {
      email: fromDom('email', raw.email),
      password: fromDom('password', raw.password),
    }
    if (env.turnstileSiteKey) {
      // Store credentials and show verification dialog
      pendingCredentials.current = data
      setTurnstileStatus('verifying')
      setShowTurnstileDialog(true)
    } else {
      // No Turnstile configured, submit directly
      signIn({ ...data, turnstileToken: undefined })
    }
  }

  // Handle Turnstile success
  const handleTurnstileSuccess = (token: string) => {
    setTurnstileStatus('success')
    // Small delay to show success state before submitting
    setTimeout(() => {
      if (pendingCredentials.current) {
        signIn({ ...pendingCredentials.current, turnstileToken: token })
        setShowTurnstileDialog(false)
      }
    }, 500)
  }

  // Handle Turnstile error
  const handleTurnstileError = () => {
    setTurnstileStatus('error')
  }

  // Reset dialog state when closed
  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setShowTurnstileDialog(false)
      setTurnstileStatus('pending')
      pendingCredentials.current = null
    }
  }

  // Close dialog if sign-in error occurs
  useEffect(() => {
    if (signInError) {
      setShowTurnstileDialog(false)
      setTurnstileStatus('pending')
    }
  }, [signInError])

  return (
    <div className="w-full">
      {/* DEV-ONLY: masquerade widget (local scaffolding, do not ship) */}
      {import.meta.env.DEV && <DevMasquerade />}

      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-serif text-4xl font-normal tracking-[0.2em] mb-4">Arthaus</h1>
        <h2 className="text-2xl font-semibold mb-2">Welcome Back</h2>
        <p className="text-muted-foreground text-sm">
          Log in to access the artist & admin dashboard.
        </p>
      </div>

      <Form {...form}>
        <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          {signInError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{getErrorMessage(signInError)}</AlertDescription>
            </Alert>
          )}

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-foreground">
                  Email Address
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      {...field}
                      disabled={isSigningIn}
                      className="pl-10 h-12"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel className="text-sm font-medium text-foreground">Password</FormLabel>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <FormControl>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      {...field}
                      disabled={isSigningIn}
                      className="pl-10 pr-10 h-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full h-12 text-base font-medium"
            disabled={isSigningIn}
          >
            {isSigningIn ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Logging in...
              </>
            ) : (
              'Log In'
            )}
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or continue with</span>
            </div>
          </div>

          {ssoError && (
            <p className="mb-3 text-center text-sm text-destructive" role="alert">
              {ssoError}
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Button
              type="button"
              variant="outline"
              className="h-12"
              disabled={isSigningIn || !!ssoProvider}
              onClick={() => startSso('google')}
            >
              <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google
            </Button>

            <Button
              type="button"
              variant="outline"
              className="h-12"
              disabled={isSigningIn || !!ssoProvider}
              onClick={() => startSso('facebook')}
            >
              <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              Facebook
            </Button>
          </div>

          <div className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-foreground hover:underline font-medium">
              Apply to Join
            </Link>
          </div>
        </form>
      </Form>

      {/* Turnstile Verification Dialog */}
      <Dialog open={showTurnstileDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Security Verification
            </DialogTitle>
            <DialogDescription>
              Please complete the security check to continue signing in.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center gap-4 py-4">
            {turnstileStatus === 'verifying' && (
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <Turnstile
                    siteKey={env.turnstileSiteKey}
                    onSuccess={handleTurnstileSuccess}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileError}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Verifying you're human...
                </p>
              </div>
            )}

            {turnstileStatus === 'success' && (
              <div className="text-center py-4">
                <div className="flex justify-center mb-3">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <ShieldCheck className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <p className="text-sm font-medium text-green-600">Verification successful!</p>
                <p className="text-sm text-muted-foreground mt-1">Signing you in...</p>
              </div>
            )}

            {turnstileStatus === 'error' && (
              <div className="text-center py-4">
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Verification failed. Please try again.
                  </AlertDescription>
                </Alert>
                <Button
                  variant="outline"
                  onClick={() => setTurnstileStatus('verifying')}
                  className="mr-2"
                >
                  Try Again
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => handleDialogClose(false)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {turnstileStatus === 'verifying' && (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDialogClose(false)}
              >
                Cancel and go back
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

